import {
  AlertCircle,
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  Clock,
  ExternalLink,
  Globe,
  Loader2,
  type LucideIcon,
  MessageSquare,
  Search,
  Send,
  Share2,
  ShieldAlert,
  Telescope,
  Trash2,
  XCircle,
  Zap,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { EntityShareDialog, PageHeader, TagPopover, useToast } from "@/components/shared";
import { Markdown } from "@/components/shared/Markdown";
import { useOperations } from "@/context/OperationContext";
import { type OperationEvent, useOperationStream } from "@/hooks/useOperationStream";
import { deepResearchService } from "@/lib/services";
import type { DeepResearchResponse, DeepResearchStatus, DeepResearchSummary } from "@/types";

// ---------------------------------------------------------------------------
// Status config
// ---------------------------------------------------------------------------

const statusConfig: Record<DeepResearchStatus, { bg: string; color: string; icon: LucideIcon; label: string }> = {
  pending: { bg: "bg-surface-tertiary/20", color: "text-content-tertiary", icon: Clock, label: "Pending" },
  submitted: { bg: "bg-blue-500/20", color: "text-blue-400", icon: Loader2, label: "Submitted" },
  in_progress: { bg: "bg-purple-500/20", color: "text-purple-400", icon: Loader2, label: "Researching" },
  completed: { bg: "bg-green-500/20", color: "text-green-400", icon: CheckCircle2, label: "Completed" },
  failed: { bg: "bg-red-500/20", color: "text-red-400", icon: AlertCircle, label: "Failed" },
  cancelled: { bg: "bg-surface-tertiary/20", color: "text-content-tertiary", icon: XCircle, label: "Cancelled" },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function StatusBadge({ status }: Readonly<{ status: DeepResearchStatus }>) {
  const cfg = statusConfig[status] ?? statusConfig.pending;
  const Icon = cfg.icon;
  const isAnimated = status === "submitted" || status === "in_progress";
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.color}`}
    >
      <Icon className={`w-3 h-3 ${isAnimated ? "animate-spin" : ""}`} />
      {cfg.label}
    </span>
  );
}

function formatDate(iso?: string) {
  if (!iso) return "--";
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function tokenDisplay(input: number, output: number) {
  if (input === 0 && output === 0) return null;
  const fmt = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));
  return `${fmt(input)} in / ${fmt(output)} out`;
}

// ---------------------------------------------------------------------------
// Example prompts
// ---------------------------------------------------------------------------

const EXAMPLE_QUERIES = [
  "What are the latest developments in quantum computing and what companies are leading the space?",
  "Comprehensive analysis of the global EV market: key players, growth forecasts, and regulatory trends for 2026",
  "Research the current state of AI regulation worldwide, comparing approaches across the US, EU, and China",
];

// ---------------------------------------------------------------------------
// Detail body — extracted to reduce component complexity
// ---------------------------------------------------------------------------

function DetailBody({ detail, detailLoading }: { detail: DeepResearchResponse | null; detailLoading: boolean }) {
  // NOSONAR -- status-based conditional rendering
  if (detailLoading && !detail) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
      </div>
    );
  }

  if (detail?.status === "completed" && detail.resultContent) {
    return <CompletedReport detail={detail} />;
  }

  if (detail?.status === "failed") {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <AlertCircle className="w-10 h-10 text-red-400 mb-3" />
        <p className="text-content-secondary">Research failed</p>
        <p className="text-sm text-content-quaternary mt-1 max-w-md">
          {detail.errorMessage || "An unknown error occurred."}
        </p>
      </div>
    );
  }

  if (detail?.status === "cancelled") {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <XCircle className="w-10 h-10 text-content-tertiary mb-3" />
        <p className="text-content-secondary">Research cancelled</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center h-64 text-center">
      <Loader2 className="w-10 h-10 animate-spin text-purple-400 mb-3" />
      <p className="text-content-secondary">Research in progress...</p>
      <p className="text-sm text-content-quaternary mt-1">
        This typically takes 2-5 minutes. The page will update automatically.
      </p>
    </div>
  );
}

function CompletedReport({ detail }: { detail: DeepResearchResponse }) {
  return (
    <div className="max-w-3xl mx-auto px-2">
      {!!detail.metadata?.incomplete && (
        <div className="mb-6 flex items-start gap-3 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
          <ShieldAlert className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-yellow-300">Partial result</p>
            <p className="text-xs text-yellow-400/80 mt-0.5">
              {String(detail.metadata.incomplete_reason) === "content_filter"
                ? "Azure's content filter flagged this response. The report may be incomplete or replaced with a refusal message."
                : "The model stopped before completing the full report. Results may be truncated."}
            </p>
          </div>
        </div>
      )}

      {tokenDisplay(detail.inputTokens, detail.outputTokens) && (
        <div className="mb-6 flex items-center gap-2 text-xs text-content-quaternary">
          <Zap className="w-3.5 h-3.5" />
          {tokenDisplay(detail.inputTokens, detail.outputTokens)}
        </div>
      )}

      <div className="deep-research-report">
        <Markdown content={detail.resultContent!} className="prose-lg" />
      </div>

      {detail.citations.length > 0 && (
        <div className="mt-8 border-t border-border-primary pt-6">
          <h3 className="text-sm font-semibold text-content-primary mb-3 flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            Sources ({detail.citations.length})
          </h3>
          <div className="space-y-2">
            {detail.citations.map((c) => (
              <a
                key={c.url}
                href={c.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-2 p-2 rounded-lg hover:bg-interactive-hover transition group"
              >
                <Globe className="w-4 h-4 mt-0.5 text-content-quaternary group-hover:text-brand-primary shrink-0" />
                <div className="min-w-0">
                  <span className="text-sm text-content-secondary group-hover:text-content-primary block truncate">
                    {c.title || c.url}
                  </span>
                  <span className="text-xs text-content-quaternary truncate block">{c.url}</span>
                </div>
                <ExternalLink className="w-3.5 h-3.5 text-content-quaternary group-hover:text-brand-primary shrink-0 ml-auto mt-0.5" />
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function DeepResearch() {
  // NOSONAR -- page component with list/detail views, operation stream, and CRUD
  const { showToast } = useToast();
  const { trackOperation, findByEntityId } = useOperations();
  const { requestId } = useParams<{ requestId?: string }>();
  const navigate = useNavigate();

  // List state
  const [requests, setRequests] = useState<DeepResearchSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // Detail view — driven by URL param
  const selectedUuid = requestId ?? null;
  const [detail, setDetail] = useState<DeepResearchResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);

  const selectRequest = (uuid: string) => navigate(`/deep-research/${uuid}`);
  const clearSelection = () => navigate("/deep-research");

  // Create form
  const [newQuery, setNewQuery] = useState("");
  const [creating, setCreating] = useState(false);

  // Operation stream — tracks the currently active operation (if any)
  const [operationId, setOperationId] = useState<string | null>(null);

  // Ref to hold the uuid of the request being tracked by the operation
  const trackedUuidRef = useRef<string | null>(null);

  // ---- Load list --------------------------------------------------------
  const loadRequests = useCallback(async () => {
    try {
      const res = await deepResearchService.list(1, 100);
      setRequests(res.data);
    } catch {
      showToast("Failed to load research requests", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  // ---- Load detail ------------------------------------------------------
  const loadDetail = useCallback(async (uuid: string) => {
    try {
      const res = await deepResearchService.get(uuid);
      setDetail(res.data);
    } catch {
      // Silently fail on refresh — only show error on initial load
    }
  }, []);

  useEffect(() => {
    if (!selectedUuid) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setDetailLoading(true);
      try {
        const res = await deepResearchService.get(selectedUuid);
        if (!cancelled) setDetail(res.data);
      } catch {
        showToast("Failed to load research result", "error");
        if (!cancelled) clearSelection();
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedUuid, showToast]);

  // ---- Operation stream event handler -----------------------------------
  const handleStatusUpdate = useCallback(
    (event: OperationEvent) => {
      const uuid = event.data.uuid as string;
      const newStatus = event.data.status as DeepResearchStatus;

      setRequests((prev) => prev.map((r) => (r.uuid === uuid ? { ...r, status: newStatus } : r)));

      if (uuid === selectedUuid || uuid === trackedUuidRef.current) {
        loadDetail(uuid);
      }
    },
    [selectedUuid, loadDetail],
  );

  const handleOperationTerminal = useCallback(() => {
    loadRequests();

    const uuid = trackedUuidRef.current;
    if (uuid && uuid === selectedUuid) {
      loadDetail(uuid);
    }

    setOperationId(null);
    trackedUuidRef.current = null;
  }, [selectedUuid, loadDetail, loadRequests]);

  const handleOperationEvent = useCallback(
    (event: OperationEvent) => {
      if (event.event === "status_update") {
        handleStatusUpdate(event);
      } else if (event.event === "operation_completed" || event.event === "operation_failed") {
        handleOperationTerminal();
      }
    },
    [handleStatusUpdate, handleOperationTerminal],
  );

  useOperationStream(operationId, {
    onEvent: handleOperationEvent,
  });

  // ---- Reconnect to an in-progress operation when navigating to the detail
  // page (e.g. the user clicked an in-progress item, or navigated away and
  // back while research was still running). Without this, operationId is null
  // after navigation resets component state, so the SSE stream never reopens
  // and the page stays stuck at the last-fetched status.
  // Mirrors the same pattern used by TaskDetail.tsx.
  useEffect(() => {
    if (!selectedUuid || operationId) return;
    const activeOp = findByEntityId(selectedUuid);
    if (activeOp) {
      trackedUuidRef.current = selectedUuid;
      setOperationId(activeOp.operation_id);
    }
  }, [selectedUuid, findByEntityId, operationId]);

  // ---- Fallback polling for requests started in other tabs/sessions -----
  // Only poll if there are active requests that we don't have an operationId for.
  // When viewing a detail page for a request with no live operation stream,
  // also refresh the detail so status changes are reflected without navigation.
  useEffect(() => {
    const hasActive = requests.some(
      (r) =>
        (r.status === "submitted" || r.status === "in_progress" || r.status === "pending") &&
        r.uuid !== trackedUuidRef.current,
    );
    if (!hasActive) return;
    const interval = setInterval(() => {
      loadRequests();
      // Also refresh the detail if we're viewing a request that isn't tracked
      // by an operation stream (e.g. started in another session/tab).
      if (selectedUuid && selectedUuid !== trackedUuidRef.current) {
        loadDetail(selectedUuid);
      }
    }, 10_000);
    return () => clearInterval(interval);
  }, [requests, loadRequests, loadDetail, selectedUuid]);

  // ---- Create -----------------------------------------------------------
  const handleCreate = async () => {
    if (!newQuery.trim()) return;
    setCreating(true);
    try {
      const res = await deepResearchService.create({ query: newQuery.trim() });
      showToast("Research request submitted", "success");
      setNewQuery("");
      prependRequest(res.data);
      startOperationTracking(res);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to submit";
      showToast(msg, "error");
    } finally {
      setCreating(false);
    }
  };

  const prependRequest = (data: DeepResearchResponse) => {
    setRequests((prev) => [
      {
        uuid: data.uuid,
        title: data.title ?? undefined,
        query: data.query,
        status: data.status,
        modelName: data.modelName,
        inputTokens: data.inputTokens,
        outputTokens: data.outputTokens,
        submittedAt: data.submittedAt ?? undefined,
        completedAt: data.completedAt ?? undefined,
        createdAt: data.createdAt,
      },
      ...prev,
    ]);
  };

  const startOperationTracking = (res: { operation_id?: string; data: DeepResearchResponse }) => {
    if (!res.operation_id) return;
    trackedUuidRef.current = res.data.uuid;
    setOperationId(res.operation_id);
    trackOperation({
      operation_id: res.operation_id,
      operation_type: "deep_research",
      entity_id: res.data.uuid,
      status: "running",
      title: newQuery.slice(0, 50) || "Deep research",
      started_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      user_id: 0,
    });
  };

  // ---- Delete -----------------------------------------------------------
  const handleDelete = async (uuid: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await deepResearchService.delete(uuid);
      setRequests((prev) => prev.filter((r) => r.uuid !== uuid));
      if (selectedUuid === uuid) clearSelection();
      showToast("Research request deleted", "success");
    } catch {
      showToast("Failed to delete", "error");
    }
  };

  // ---- Cancel -----------------------------------------------------------
  const handleCancel = async (uuid: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await deepResearchService.cancel(uuid);
      // If we're tracking this request, the operation stream will pick up the cancellation.
      // Otherwise, refresh the list.
      if (uuid !== trackedUuidRef.current) {
        loadRequests();
      }
      showToast("Research request cancelled", "success");
    } catch {
      showToast("Failed to cancel", "error");
    }
  };

  // ---- Filter -----------------------------------------------------------
  const filtered = requests.filter((r) => {
    const q = searchQuery.toLowerCase();
    return r.query.toLowerCase().includes(q) || (r.title?.toLowerCase().includes(q) ?? false);
  });

  // =====================================================================
  // DETAIL VIEW
  // =====================================================================
  if (selectedUuid) {
    return (
      <div className="h-full flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 p-6 pb-4 border-b border-border-primary">
          <button
            onClick={() => clearSelection()}
            className="p-2 rounded-lg hover:bg-interactive-hover text-content-tertiary hover:text-content-primary transition"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-semibold text-content-primary truncate">{detail?.title || "Deep Research"}</h1>
            <p className="text-sm text-content-tertiary mt-0.5 truncate">{detail?.query}</p>
          </div>
          {detail && <StatusBadge status={detail.status} />}
          {detail?.status === "completed" && detail.resultContent && (
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => {
                  const prefill = `I have a deep research report on "${detail.query}". Here is the full report for context:\n\n---\n\n${detail.resultContent}\n\n---\n\nBased on this research, `;
                  navigate("/chat", { state: { prefill } });
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border-primary text-content-secondary hover:text-content-primary hover:border-purple-500/50 hover:bg-purple-500/10 transition"
                title="Continue this research in a chat conversation"
              >
                <MessageSquare className="w-3.5 h-3.5" />
                Send to Chat
              </button>
            </div>
          )}
          {detail && <TagPopover entityType="deep_research" entityId={detail.uuid} mode="compact" />}
          {detail && (
            <button
              onClick={() => setIsShareDialogOpen(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-surface-secondary hover:bg-interactive-active border border-border-secondary text-content-tertiary hover:text-content-primary rounded-lg transition text-sm"
              title="Share this research"
            >
              <Share2 className="w-4 h-4" />
              <span className="hidden sm:inline">Share</span>
            </button>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          <DetailBody detail={detail} detailLoading={detailLoading} />
        </div>

        {detail && (
          <EntityShareDialog
            entityId={detail.uuid}
            entityTitle={detail.title || detail.query || "Deep Research"}
            isOpen={isShareDialogOpen}
            onClose={() => setIsShareDialogOpen(false)}
            entityLabel="Deep Research"
            entityType="deep_research"
            shareLinkPath="/deep-research/shared/"
            service={deepResearchService}
          />
        )}
      </div>
    );
  }

  // =====================================================================
  // LIST VIEW
  // =====================================================================
  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <PageHeader
        icon={Telescope}
        title="Deep Research"
        subtitle="Submit complex research questions and get comprehensive, citation-backed reports."
      >
        {/* Submit form */}
        <div className="mt-4 bg-surface-secondary rounded-xl p-4 border border-border-primary">
          <div className="flex gap-3">
            <textarea
              value={newQuery}
              onChange={(e) => setNewQuery(e.target.value)}
              placeholder="What would you like to research?"
              rows={2}
              className="flex-1 px-4 py-3 bg-surface-primary border border-border-primary rounded-lg text-content-primary placeholder-content-quaternary resize-none focus:outline-hidden focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary text-sm"
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleCreate();
              }}
            />
            <button
              onClick={handleCreate}
              disabled={!newQuery.trim() || creating}
              className="px-5 py-3 bg-purple-600 hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg transition flex items-center gap-2 self-end text-sm font-medium"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Research
            </button>
          </div>
          <p className="text-xs text-content-quaternary mt-2">
            Press{" "}
            <kbd className="px-1 py-0.5 rounded-sm bg-surface-tertiary text-content-tertiary text-[10px]">
              Cmd+Enter
            </kbd>{" "}
            to submit. Powered by <span className="text-purple-400">o3-deep-research</span> with web search.
          </p>
        </div>

        {/* Example queries */}
        {requests.length === 0 && !loading && (
          <div className="mt-4">
            <p className="text-xs text-content-quaternary mb-2">Try one of these:</p>
            <div className="flex flex-wrap gap-2">
              {EXAMPLE_QUERIES.map((q) => (
                <button
                  key={q}
                  onClick={() => setNewQuery(q)}
                  className="text-xs px-3 py-1.5 rounded-full border border-border-primary text-content-tertiary hover:text-content-primary hover:border-purple-500/50 hover:bg-purple-500/10 transition"
                >
                  {q.length > 80 ? `${q.slice(0, 80)}...` : q}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Search */}
        {requests.length > 0 && (
          <div className="mt-4 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-quaternary" />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search research requests..."
              className="w-full pl-10 pr-4 py-2 bg-surface-primary border border-border-primary rounded-lg text-sm text-content-primary placeholder-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/40"
            />
          </div>
        )}
      </PageHeader>

      {/* List */}
      <div className="flex-1 overflow-y-auto p-6">
        {(() => {
          if (loading) {
            return (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
              </div>
            );
          }
          if (filtered.length === 0) {
            return (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <BookOpen className="w-10 h-10 text-content-quaternary mb-3" />
                <p className="text-content-tertiary">
                  {searchQuery ? "No matching research requests" : "No research requests yet"}
                </p>
                <p className="text-sm text-content-quaternary mt-1">Submit a research question above to get started.</p>
              </div>
            );
          }
          return (
            <div className="space-y-3">
              {filtered.map((req) => {
                const canCancel =
                  req.status === "pending" || req.status === "submitted" || req.status === "in_progress";
                const isTerminal = req.status === "completed" || req.status === "failed" || req.status === "cancelled";

                return (
                  <div
                    key={req.uuid}
                    role="button" // NOSONAR — contains nested interactive elements
                    tabIndex={0}
                    onClick={() => selectRequest(req.uuid)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        selectRequest(req.uuid);
                      }
                    }}
                    className="bg-surface-secondary border border-border-primary rounded-xl p-4 hover:border-purple-500/30 cursor-pointer transition group"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <StatusBadge status={req.status} />
                          {tokenDisplay(req.inputTokens, req.outputTokens) && (
                            <span className="text-[10px] text-content-quaternary flex items-center gap-1">
                              <Zap className="w-3 h-3" />
                              {tokenDisplay(req.inputTokens, req.outputTokens)}
                            </span>
                          )}
                        </div>
                        <h3 className="text-sm font-medium text-content-primary truncate mt-1">
                          {req.title || req.query}
                        </h3>
                        {req.title && <p className="text-xs text-content-quaternary truncate mt-0.5">{req.query}</p>}
                        <p className="text-xs text-content-quaternary mt-1">
                          {formatDate(req.createdAt)}
                          {req.completedAt && ` — completed ${formatDate(req.completedAt)}`}
                        </p>
                      </div>

                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                        {canCancel && (
                          <button
                            onClick={(e) => handleCancel(req.uuid, e)}
                            className="p-1.5 rounded-lg hover:bg-yellow-500/20 text-content-quaternary hover:text-yellow-400 transition"
                            title="Cancel"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        )}
                        {isTerminal && (
                          <button
                            onClick={(e) => handleDelete(req.uuid, e)}
                            className="p-1.5 rounded-lg hover:bg-red-500/20 text-content-quaternary hover:text-red-400 transition"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>
    </div>
  );
}
